import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

const STATUS_COLORS = {
  pending:  { bg: '#fef9c3', color: '#854d0e', label: 'Pending' },
  approved: { bg: '#f0fdf4', color: '#166534', label: 'Approved' },
  rejected: { bg: '#fef2f2', color: '#991b1b', label: 'Rejected' },
  expired:  { bg: '#f3f4f6', color: '#374151', label: 'Expired' },
}

const RISK_COLORS = {
  low:    { bg: '#f0fdf4', color: '#166534' },
  medium: { bg: '#fef9c3', color: '#854d0e' },
  high:   { bg: '#fef2f2', color: '#991b1b' },
}

export default function AdminUserList({ admin, onSelectUser, lang }) {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterType, setFilterType] = useState('all')

  const t = (en, zh) => lang === 'zh' ? zh : en

  useEffect(() => { fetchUsers() }, [])

  const fetchUsers = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false })
    setUsers(data || [])
    setLoading(false)
  }

  const filtered = users.filter(u => {
    const matchSearch = !search ||
      u.user_number_text?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
    const matchStatus = filterStatus === 'all' || u.kyc_status === filterStatus
    const matchType = filterType === 'all' || u.customer_type === filterType
    return matchSearch && matchStatus && matchType
  })

  const Badge = ({ status, type = 'kyc' }) => {
    const map = type === 'risk' ? RISK_COLORS : STATUS_COLORS
    const c = map[status] || map.pending
    return (
      <span style={{ background: c.bg, color: c.color, padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' }}>
        {type === 'risk' ? status?.toUpperCase() : (c.label || status)}
      </span>
    )
  }

  return (
    <div>
      {/* 统计卡片 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: t('Total Users', '总用户'), value: users.length, color: '#3b82f6' },
          { label: t('Pending Review', '待审核'), value: users.filter(u => u.kyc_status === 'pending').length, color: '#f59e0b' },
          { label: t('Approved', '已通过'), value: users.filter(u => u.kyc_status === 'approved').length, color: '#10b981' },
          { label: t('Rejected', '已拒绝'), value: users.filter(u => u.kyc_status === 'rejected').length, color: '#ef4444' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '10px', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderLeft: `4px solid ${s.color}` }}>
            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>{s.label}</p>
            <p style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* 搜索和筛选 */}
      <div style={{ background: '#fff', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('Search by ID or email...', '搜索用户编号或邮箱...')}
          style={{ flex: 1, minWidth: '200px', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }}
        />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }}>
          <option value="all">{t('All Status', '全部状态')}</option>
          <option value="pending">{t('Pending', '待审核')}</option>
          <option value="approved">{t('Approved', '已通过')}</option>
          <option value="rejected">{t('Rejected', '已拒绝')}</option>
          <option value="expired">{t('Expired', '已过期')}</option>
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }}>
          <option value="all">{t('All Types', '全部类型')}</option>
          <option value="individual">KYC</option>
          <option value="corporate">KYB</option>
        </select>
        <button onClick={fetchUsers}
          style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
          🔄 {t('Refresh', '刷新')}
        </button>
      </div>

      {/* 用户列表 */}
      <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              {[
                t('User ID', '用户编号'),
                t('Email', '邮箱'),
                t('Type', '类型'),
                t('KYC Status', 'KYC状态'),
                t('Risk Level', '风险等级'),
                t('Registered', '注册时间'),
                t('Action', '操作'),
              ].map((h, i) => (
                <th key={i} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>{t('No users found', '没有找到用户')}</td></tr>
            ) : filtered.map(u => (
              <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background .15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                  {u.user_number_text || '—'}
                </td>
                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#374151' }}>{u.email}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ background: u.customer_type === 'individual' ? '#eff6ff' : '#f5f3ff', color: u.customer_type === 'individual' ? '#1d4ed8' : '#6d28d9', padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' }}>
                    {u.customer_type === 'individual' ? 'KYC' : 'KYB'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px' }}><Badge status={u.kyc_status} /></td>
                <td style={{ padding: '12px 16px' }}><Badge status={u.risk_level} type="risk" /></td>
                <td style={{ padding: '12px 16px', fontSize: '12px', color: '#6b7280' }}>
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <button onClick={() => onSelectUser(u)}
                    style={{ padding: '5px 14px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '500' }}>
                    {t('Review', '审核')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}